import type { EventStyles, Nullable } from '../types';

export type RgbColor = Array<number> | undefined;

const getLuminanace = (values: RgbColor): number => {
  if (!values) {
    return 0;
  }

  const rgb = values.map((v) => {
    const val = v / 255;
    return val <= 0.03928 ? val / 12.92 : ((val + 0.055) / 1.055) ** 2.4;
  });

  return Number((0.2126 * rgb[0] + 0.7152 * rgb[1] + 0.0722 * rgb[2]).toFixed(3));
};

const hexToRgb = (hexColor: string): RgbColor => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hexColor);
  return result ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)] : undefined;
};

const getContrastRatio = (colorA: string, colorB: string): number => {
  const lumA = getLuminanace(hexToRgb(colorA));
  const lumB = getLuminanace(hexToRgb(colorB));
  return (Math.max(lumA, lumB) + 0.05) / (Math.min(lumA, lumB) + 0.05);
};

export const isContrastLow = (colorA: string, colorB = '#ffffff'): boolean => getContrastRatio(colorA, colorB) < 4.5;

/**
 * Injects custom  CSS to DOM
 * @param branding {Object}
 * @return void
 */
export const injectBrandingColors = (branding: Nullable<EventStyles>): void => {
  if (!branding) {
    return;
  }

  const styleElement = document.createElement('style');
  document.head.appendChild(styleElement);
  const { primaryColor, primaryFontColor, secondaryColor, secondaryFontColor, tertiaryColor, tertiaryFontColor } =
    branding;
  const styles = [
    `.custom-primary-text-color { color: ${primaryFontColor} !important; }`,
    `.custom-secondary-text-color { color: ${secondaryFontColor} !important; }`,
    `.custom-tertiary-text-color { color: ${tertiaryFontColor} !important; }`,
    `.custom-primary-color { background-color: ${primaryColor} !important; }`,
    `.custom-secondary-color { background-color: ${secondaryColor} !important; }`,
    `.custom-tertiary-color { background-color: ${tertiaryColor} !important; }`,
    `.custom-primary-bg-as-color { color: ${primaryColor} !important; }`,
    `.custom-secondary-bg-as-color { color: ${secondaryColor} !important; }`,
    `.custom-tertiary-bg-as-color { color: ${tertiaryColor} !important; }`,
    `.custom-primary-border-color { border-color: ${primaryColor} !important; }`,
    `.custom-secondary-border-color { border-color: ${secondaryColor} !important; }`,
    `.custom-tertiary-border-color { border-color: ${tertiaryFontColor} !important; }`,
    `.custom-primary-button { color: ${primaryFontColor} !important; background-color: ${primaryColor} !important; border-color: ${primaryColor} !important; }`,
    `.custom-secondary-button { color: ${secondaryFontColor} !important; background-color: ${secondaryColor} !important; border-color: ${secondaryColor} !important; }`,
    `.custom-tertiary-button { color: ${tertiaryFontColor} !important; background-color: ${tertiaryColor} !important; border-color: ${tertiaryColor} !important; }`,
    '.custom-primary-button:hover, .custom-primary-button:focus, .custom-secondary-button:hover, .custom-secondary-button:focus, .custom-tertiary-button:hover, .custom-tertiary-button:focus { filter: brightness(90%); }',
  ];

  /*
   FIXME:
   Currently v1 is not comparing primary color with branding background color
   (passed here as backgroundColor), but with default color (#ffffff)
   */
  if (!isContrastLow(primaryColor)) {
    // RR Logo color
    styles.push(`a:hover, a:focus { color: ${primaryColor} }`);
    styles.push(`.navbar__svg-logo-fill { fill: ${primaryColor} !important; }`);
  }

  styleElement.innerHTML = styles.join('\n');
};
